import React, { useState } from 'react';
import { logError } from '../lib/logger';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Shield } from 'lucide-react';
import { toast } from 'sonner';
import SEO, { faqJsonLd } from '../components/SEO';
import { trackEvent } from '../lib/analytics';

const warrantyFaqs = [
  {
    question: 'How long is the CTRL Systems product warranty?',
    answer:
      'CTRL Systems products are covered by a 6-year extended limited warranty from the date of purchase from CTRL or an authorized distributor. The device must be registered within one year of purchase and must receive its required biennial factory calibration to keep the warranty in force.',
  },
  {
    question: 'What is required to maintain the 6-year warranty?',
    answer:
      'To maintain the 6-year extended limited warranty, covered devices must receive a factory calibration from CTRL Systems every two years. If a biennial calibration lapses, ongoing warranty coverage is voided.',
  },
  {
    question: 'What does the CTRL Systems warranty cover?',
    answer:
      'The warranty covers defects in materials and workmanship and conformance to CTRL\'s written specifications. Products found to be defective or non-conforming during the warranty period will be repaired or replaced at CTRL\'s option, at no charge if delivered prepaid with proof of purchase and prior authorization.',
  },
  {
    question: 'What voids the warranty?',
    answer:
      'The warranty is void in the event of unauthorized repair or modification, missed biennial calibrations, normal wear, abuse, misuse, negligence, accident, shipping damage, improper storage, or failure to use the product in accordance with instructions.',
  },
  {
    question: 'Which products are covered?',
    answer:
      'Covered products are the CTRL UL101 Standard Model (UL101 Receiver, UT2000 Transmitter and all kit components), CTRL UL101 Intrinsically Safe Model (UL101-IS Receiver, UT2000 Transmitter-IS and all kit components), and PowerBeam 300.',
  },
  {
    question: 'How do I request warranty service?',
    answer:
      'Contact CTRL Systems at 1004 Littlestown Pike, Suite H, Westminster, MD 21157 USA — 1.410.876.5676 or toll free 1.877.287.5707 (US and Canada) — to obtain prior written approval before returning any product for warranty service.',
  },
];

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const initialFormState = {
  name: '',
  company: '',
  email: '',
  phone: '',
  sending_address: '',
  service_type: 'Calibration',
  notes: '',
  mailing_list_opt_in: false,
};

const initialRegistrationState = {
  company: '',
  company_contact: '',
  contact_title: '',
  contact_email: '',
  company_address: '',
  kit_number: '',
  ul101_serial: '',
  ut2000_serial: '',
};

const WarrantyPage = ({ onRequestDemo, onRequestQuote }) => {
  const [showForm, setShowForm] = useState(false);
  const [showRegistration, setShowRegistration] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [registering, setRegistering] = useState(false);
  const [formData, setFormData] = useState(initialFormState);
  const [regData, setRegData] = useState(initialRegistrationState);

  const updateField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const updateRegField = (field, value) => {
    setRegData((prev) => ({ ...prev, [field]: value }));
  };

  const handleRegistrationSubmit = async (e) => {
    e.preventDefault();
    setRegistering(true);
    try {
      const payload = {
        ...regData,
        kit_number: regData.kit_number.trim() === '' ? null : regData.kit_number.trim(),
      };
      const { data } = await axios.post(`${API}/registration/submit`, payload);
      toast.success(data?.message || 'Registration received.');
      setRegData(initialRegistrationState);
      setShowRegistration(false);
    } catch (err) {
      logError('Product registration failed', err);
      toast.error(
        err?.response?.data?.detail ||
          'Something went wrong submitting your registration. Please email jwatts@ctrlsys.com directly.'
      );
    } finally {
      setRegistering(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/rma/submit`, formData);
      toast.success(data?.message || 'Your RMA request has been submitted.');
      setFormData(initialFormState);
      setShowForm(false);
    } catch (err) {
      logError('RMA submission failed', err);
      toast.error(
        err?.response?.data?.detail ||
          'Something went wrong submitting your request. Please email jwatts@ctrlsys.com directly.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Warranty — ctrl.aero"
        description="CTRL Systems six-year parts and labor warranty for the UL101 Aviation Troubleshooter. Register your product and file a warranty claim."
        jsonLd={faqJsonLd(warrantyFaqs)}
      />
      <Header onRequestDemo={onRequestDemo} onRequestQuote={onRequestQuote} />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800">
        <div className="absolute inset-0 opacity-20">
          <img
            src="/images/img_0235.jpeg"
            alt="Aviation"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
            CTRL Systems Product Warranty
          </h1>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto">
            6-Year Extended Limited Warranty for CTRL Systems-manufactured ultrasound sensors
          </p>
        </div>
      </section>

      {/* Warranty Content */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6 mb-10">
            <div className="flex items-center">
              <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center mr-4">
                <Shield className="w-7 h-7 text-white" />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-[#1e2d3b]">
                  CTRL Systems Warranty Details: 6-Year Extended Limited Warranty
                </h2>
                <p className="text-slate-500 mt-1 text-sm">
                  Device must be registered within one year of purchase
                </p>
              </div>
            </div>
            <a
              href="#register-product"
              onClick={(e) => {
                e.preventDefault();
                trackEvent('warranty_register_open', { product: 'UL101' });
                setShowRegistration(true);
              }}
              data-testid="register-product-link"
              className="inline-flex items-center justify-center rounded-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-semibold text-sm px-5 py-2.5 shadow-md transition-all cursor-pointer"
            >
              Register My UL101 (B00033)
            </a>
          </div>

          <div className="bg-blue-50 border-l-4 border-blue-600 rounded-r-lg p-6 mb-10">
            <p className="text-slate-800 font-semibold mb-1">Required biennial calibration</p>
            <p className="text-slate-700">
              To maintain the 6-year extended limited warranty, covered devices must receive a
              factory calibration every two years. Lapsed calibrations void ongoing warranty
              coverage.
            </p>
          </div>

          <div className="prose prose-slate max-w-none text-slate-700 leading-relaxed space-y-6">
            <p>
              Products* manufactured by CTRL Systems, Inc. (CTRL) are warranted for six years from
              date of purchase (the &ldquo;Warranty Period&rdquo;) from CTRL or an authorized
              distributor/dealer representative to be free of defects in materials and workmanship
              and to conform to CTRL&rsquo;s written specifications, provided the device has
              received its required biennial calibration.
            </p>
            <p>
              At CTRL&rsquo;s option, any such defective or nonconforming product for which prompt
              written notice of defect is given to CTRL during the Warranty Period will be repaired
              or replaced. Such repair or replacement will be at no charge to the customer if the
              defective or nonconforming product is delivered to CTRL or an authorized service
              center, prepaid, together with proof and date of purchase. Prior to return of a
              defective or nonconforming product, customer must obtain approval from CTRL Systems,
              Inc., 1004 Littlestown Pike, Suite H, Westminster, MD 21157 USA (1.410.876.5676 or
              1.877.287.5707 toll free US and Canada).
            </p>
            <p>
              If CTRL determines, in its sole and absolute discretion, that a product for which
              warranty service is claimed, is not defective or nonconforming, the customer shall
              pay CTRL&rsquo;s cost of handling, inspecting, testing and transportation. CTRL may
              use new, remanufactured, reconditioned, refurbished or functionally equivalent
              products or parts in the furnishing of repairs or replacements under this limited
              warranty. If product is repaired or replaced as provided herein the product is
              warranted for the remainder of the original Warranty Period.
            </p>
            <p>
              This warranty excludes any other defects or non-conformities of any nature
              whatsoever, including limitation, any defect due to normal wear, abuse, misuse,
              negligence, accident, shipping damage, improper storage or failure to use the product
              in accordance with instructions. This warranty is void in the event of unauthorized
              repair or modification, or if the required biennial calibration is not performed.
              This warranty also excludes third-party products not manufactured, but sold to the
              customer, by CTRL or an authorized distributor/dealer/representative; however, to the
              extent permitted, CTRL hereby assigns to customer any warranties given to CTRL by
              such third-party vendor.
            </p>
            <p className="text-sm uppercase tracking-wide font-semibold text-slate-800">
              Except to the extent precluded by applicable law, the foregoing limited warranty is
              in lieu of all other warranties, expressed or implied, including without limitation,
              any implied warranties of merchantability or fitness for a particular purpose.
              Customer&rsquo;s sole and exclusive remedy shall be CTRL&rsquo;s obligation to repair
              or replace as set forth above. In no event will CTRL be liable under any cause of
              action or claim of whatever nature, regardless of whether characterized as tort,
              negligence, contract, warranty, strict liability or otherwise, for any loss of
              profits or other economic loss, or any other indirect, consequential, incidental, or
              special damages. In no event shall CTRL&rsquo;s direct damages exceed the cost of the
              particular product alleged by the customer to be defective.
            </p>
            <p>This warranty shall be governed by the laws of the State of Maryland, U.S.A.</p>
            <p className="text-sm text-slate-500 italic">
              * CTRL products covered by warranty are as follows: CTRL UL101 Standard Model
              (includes UL101 Receiver, UT2000 Transmitter, and all kit components); CTRL UL101
              Intrinsically Safe Model (includes UL101-IS Receiver, UT2000 Transmitter-IS, and all
              kit components), and PowerBeam 300.
            </p>
          </div>
        </div>
      </section>

      {/* RMA CTA */}
      <section className="py-20 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-gradient-to-br from-blue-50 to-slate-50 rounded-2xl p-10 border-2 border-blue-100">
            <h3 className="text-3xl font-bold text-[#1e2d3b] mb-3">
              Need Calibration, Warranty, or Repair Service?
            </h3>
            <p className="text-lg text-slate-700 leading-relaxed mb-8">
              Submit an RMA request below. Our team will review and respond with return
              instructions.
            </p>
            <div className="flex flex-col gap-3">
              <Button
                onClick={() => setShowForm(true)}
                data-testid="open-rma-form-btn"
                className="w-full bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white px-8 py-3 text-[15px]"
              >
                Submit RMA Request for CTRL UL101 (B00033)
              </Button>
              <Button
                asChild
                variant="outline"
                className="w-full border-2 border-slate-300 hover:border-blue-500 text-[#1e2d3b] px-8 py-3 text-[15px] hover:bg-blue-50"
              >
                <a
                  href="https://www.fotric.com/technical-support"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="fotric-support-link"
                >
                  Contact FOTRIC Technical Support
                </a>
              </Button>
              <Button
                asChild
                variant="outline"
                className="w-full border-2 border-slate-300 hover:border-blue-500 text-[#1e2d3b] px-8 py-3 text-[15px] hover:bg-blue-50"
              >
                <a
                  href="https://dalcommtech.com/contact-us/?srsltid=AfmBOorNBzDkGaVjrLfVRBy_ZSbOfw95jXedf1easRBpNxYYgZqxwTQB"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="dalcomm-support-link"
                >
                  Contact Dalcomm Tech for Repairs
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* RMA Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[92vh] overflow-y-auto">
            <div className="p-8">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h3 className="text-3xl font-bold text-[#1e2d3b]">RMA Request</h3>
                  <p className="text-slate-500 text-sm mt-1">
                    We&rsquo;ll reply with return-shipping instructions shortly.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  aria-label="Close form"
                  data-testid="close-rma-form-btn"
                  className="text-slate-400 hover:text-slate-600 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Name *</label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => updateField('name', e.target.value)}
                      data-testid="rma-name-input"
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Company Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.company}
                      onChange={(e) => updateField('company', e.target.value)}
                      data-testid="rma-company-input"
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Email *</label>
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => updateField('email', e.target.value)}
                      data-testid="rma-email-input"
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      required
                      value={formData.phone}
                      onChange={(e) => updateField('phone', e.target.value)}
                      data-testid="rma-phone-input"
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Sending Address *
                  </label>
                  <textarea
                    required
                    value={formData.sending_address}
                    onChange={(e) => updateField('sending_address', e.target.value)}
                    rows={3}
                    placeholder="Street, City, State, ZIP, Country"
                    data-testid="rma-address-input"
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Service Type *
                  </label>
                  <select
                    required
                    value={formData.service_type}
                    onChange={(e) => updateField('service_type', e.target.value)}
                    data-testid="rma-service-type-select"
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="Calibration">Calibration</option>
                    <option value="Warranty Repair">Warranty Repair</option>
                    <option value="Out-of-Warranty Repair">Out-of-Warranty Repair</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Notes</label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => updateField('notes', e.target.value)}
                    rows={5}
                    placeholder="Describe the issue, serial numbers, or anything else we should know."
                    data-testid="rma-notes-input"
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  />
                </div>

                <label className="flex items-start gap-3 rounded-lg bg-slate-50 p-3 border border-slate-200 cursor-pointer">
                  <input
                    type="checkbox"
                    required
                    data-testid="rma-optin-checkbox"
                    checked={formData.mailing_list_opt_in}
                    onChange={(e) => updateField('mailing_list_opt_in', e.target.checked)}
                    className="mt-1 h-4 w-4 text-blue-600 rounded border-slate-300 focus:ring-blue-500"
                  />
                  <span className="text-sm text-slate-700 leading-snug">
                    <span className="text-red-600 font-semibold">*</span> Please add me to the
                    ctrl.aero mailing list for product updates, case studies, and aviation
                    maintenance insights. Required to submit.
                  </span>
                </label>

                <div className="flex gap-4 pt-2">
                  <Button
                    type="button"
                    onClick={() => setShowForm(false)}
                    variant="outline"
                    className="flex-1 border-slate-300"
                    data-testid="rma-cancel-btn"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={submitting}
                    data-testid="rma-submit-btn"
                    className="flex-1 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white disabled:opacity-60"
                  >
                    {submitting ? 'Submitting…' : 'Submit RMA Request'}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Product Registration Modal */}
      {showRegistration && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[92vh] overflow-y-auto">
            <div className="p-8">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h3 className="text-3xl font-bold text-[#1e2d3b]">
                    Register for the 6-Year Warranty
                  </h3>
                  <p className="text-slate-500 text-sm mt-1">
                    Complete within one year of purchase to activate extended coverage.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowRegistration(false)}
                  aria-label="Close registration form"
                  data-testid="close-registration-btn"
                  className="text-slate-400 hover:text-slate-600 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              <form onSubmit={handleRegistrationSubmit} className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Company *
                  </label>
                  <input
                    type="text"
                    required
                    value={regData.company}
                    onChange={(e) => updateRegField('company', e.target.value)}
                    data-testid="reg-company-input"
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Company Contact *
                    </label>
                    <input
                      type="text"
                      required
                      value={regData.company_contact}
                      onChange={(e) => updateRegField('company_contact', e.target.value)}
                      data-testid="reg-contact-input"
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Contact Title *
                    </label>
                    <input
                      type="text"
                      required
                      value={regData.contact_title}
                      onChange={(e) => updateRegField('contact_title', e.target.value)}
                      data-testid="reg-title-input"
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Contact Email *
                  </label>
                  <input
                    type="email"
                    required
                    value={regData.contact_email}
                    onChange={(e) => updateRegField('contact_email', e.target.value)}
                    data-testid="reg-email-input"
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Company Address *
                  </label>
                  <textarea
                    required
                    value={regData.company_address}
                    onChange={(e) => updateRegField('company_address', e.target.value)}
                    rows={3}
                    placeholder="Street, City, State, ZIP, Country"
                    data-testid="reg-address-input"
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Kit Number <span className="text-slate-400 font-normal">(optional)</span>
                  </label>
                  <input
                    type="text"
                    value={regData.kit_number}
                    onChange={(e) => updateRegField('kit_number', e.target.value)}
                    data-testid="reg-kit-input"
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      UL101 Receiver Serial # *
                    </label>
                    <input
                      type="text"
                      required
                      value={regData.ul101_serial}
                      onChange={(e) => updateRegField('ul101_serial', e.target.value)}
                      data-testid="reg-ul101-input"
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      UT2000 Transmitter Serial # *
                    </label>
                    <input
                      type="text"
                      required
                      value={regData.ut2000_serial}
                      onChange={(e) => updateRegField('ut2000_serial', e.target.value)}
                      data-testid="reg-ut2000-input"
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono"
                    />
                  </div>
                </div>

                <div className="flex gap-4 pt-2">
                  <Button
                    type="button"
                    onClick={() => setShowRegistration(false)}
                    variant="outline"
                    className="flex-1 border-slate-300"
                    data-testid="reg-cancel-btn"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={registering}
                    data-testid="reg-submit-btn"
                    className="flex-1 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white disabled:opacity-60"
                  >
                    {registering ? 'Submitting…' : 'Register for 6-Year Warranty'}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default WarrantyPage;
